import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';
import { BrandLogo } from '../context/BrandingContext';
import LandingNav from '../components/landing/LandingNav';
import LandingFooter from '../components/landing/LandingFooter';
import { useVisitorCity } from '../hooks/useVisitorCity';
import TheShift from '../components/landing/TheShift';
import TheProof from '../components/landing/TheProof';
import TheEngine from '../components/landing/TheEngine';
import ProfessionShowcase from '../components/landing/ProfessionShowcase';
import MidPageClose from '../components/landing/MidPageClose';
import StickyScanBar from '../components/landing/StickyScanBar';

/* ── NanoBanana2 Photography ── */
const PHOTOS = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/4f753163-b8b3-49ff-8a35-b8dd688db568/images/b9f9a9036e75121f6264e6e8ce3205ba08dacd89e22ef4af7a0d0bd2a0d7f7b2.png',
  stylist: 'https://static.prod-images.emergentagent.com/jobs/4f753163-b8b3-49ff-8a35-b8dd688db568/images/3d5c5739db95ad088acbbfbc0e161fd668f30a677bb2852bf3532a43e914ce7a.png',
  dentist: 'https://static.prod-images.emergentagent.com/jobs/4f753163-b8b3-49ff-8a35-b8dd688db568/images/dd32c1a3b21185ab8a0db9087fdaf5220b12ba9b0e6b160f94ba993e0daf2d35.png',
  testimonial: 'https://static.prod-images.emergentagent.com/jobs/4f753163-b8b3-49ff-8a35-b8dd688db568/images/4ea5bf4f3d546115dcb2d6656c1e69f0dba028e79a98cf933f76aafbb261ce6d.png',
};

const TESTIMONIALS = [
  { quote: 'I had no idea AI was deciding who gets mentioned in my city. GetChosenFirst showed me the problem and fixed it in a week. Now I\'m the first name that comes up.', name: 'Sarah Martinez', prof: 'Martinez Family Dental, Austin TX', img: PHOTOS.testimonial },
  { quote: "I saw my competitors being mentioned by ChatGPT and I wasn't even in the results. Two weeks later, I was the top result.", name: 'Mike J.', prof: `Plumber`, img: PHOTOS.hero },
  { quote: 'They showed me exactly what AI was saying about practices in my area. Now three AI platforms mention me first.', name: 'Dr. Kim', prof: 'Dentist, Portland', img: PHOTOS.dentist },
];

/* ── Scroll Reveal ── */
function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }} className={className}>
      {children}
    </motion.div>
  );
}

/* ── Counter Animation ── */
function AnimatedCounter({ target, suffix = '' }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isInView) return;
    const duration = 1500;
    const start = performance.now();
    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView, target]);
  return <span ref={ref}>{count}{suffix}</span>;
}

/* ══════════════════════════════════════════
   MAIN LANDING — THREE-ACT NARRATIVE
   Act 1: "You're respected here"
   Act 2: "The world is changing beneath your feet"
   Act 3: "We're your bridge to that future"

   Addendum 5 Section Flow:
   1. Hero
   2. Scarcity (3 seats)
   3. Profession Showcase (8-profession auto-rotation)
   4. How It Works
   4.5 Mid-Page Close (NEW)
   --- HARD CUT ---
   5. The Shift (dark)
   6. The Proof
   7. The Engine
   --- GRADIENT BACK TO WARM ---
   8. What We Build
   9. The Numbers
   9.5 Monitoring Statement
   10. Testimonial
   11. Final CTA
   12. Footer
   + Sticky Scan Bar
   ══════════════════════════════════════════ */
export default function Landing() {
  const navigate = useNavigate();
  const [scanInput, setScanInput] = useState('');
  const visitorCity = useVisitorCity('Denver');
  const [scanCount, setScanCount] = useState(null);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/scan/count`).then(r => r.json()).then(d => setScanCount(d.display_count)).catch(() => {});
  }, []);

  const handleScan = () => {
    if (scanInput.trim()) navigate('/onboarding', { state: { businessName: scanInput.trim() } });
    else navigate('/onboarding');
  };

  const goSection = (id) => { setMobileMenuOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); };

  return (
    <div className="min-h-screen" style={{ fontFamily: "'DM Sans', sans-serif" }} data-testid="landing-page">

      {/* ════════ NAVBAR ════════ */}
      <LandingNav extraNavItems={[{ label: 'How It Works', action: () => goSection('how-it-works') }]} />

      {/* ══════════════════════════════════════
          ACT 1 — "You're respected here"
          The RH warmth. Photography. Craft.
         ══════════════════════════════════════ */}

      {/* ════════ 1. HERO ════════ */}
      <HeroSection scanInput={scanInput} setScanInput={setScanInput} handleScan={handleScan} />

      {/* ════════ 2. SCARCITY — Three Seats Per City ════════ */}
      <section className="py-24 md:py-32 px-6 flex items-center justify-center" style={{ backgroundColor: '#EDE8DF' }} data-testid="scarcity-warm-section">
        <div className="text-center max-w-xl mx-auto">
          <Reveal>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.3em', color: '#3C3226', fontSize: 'clamp(24px, 4vw, 44px)', lineHeight: 1.2 }} className="mb-8">Three seats per profession, per city.</h2>
            <p style={{ color: '#5C5347', fontSize: '16px', fontWeight: 300, lineHeight: 1.8, maxWidth: '500px', margin: '0 auto' }} className="mb-8">We work with a select number of professionals in each market. When a plumber in {visitorCity} claims their seat, no other plumber in {visitorCity} can take it. First to claim, first to be positioned for recommendation.</p>
            <div className="flex items-center justify-center gap-4 mb-8">
              {[{ claimed: true, initial: 'M' }, { claimed: true, initial: 'R' }, { claimed: false, initial: '' }].map((seat, i) => (
                <div key={i} className="flex items-center justify-center" style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: seat.claimed ? '#F7F4EF' : 'transparent', border: seat.claimed ? '1px solid #D4C9B8' : '2px dashed #D4C9B8', color: '#3C3226', fontSize: '14px', fontWeight: 600 }} data-testid={`warm-seat-${i}`}>{seat.initial}</div>
              ))}
            </div>
            <button onClick={handleScan} className="inline-flex items-center gap-1" style={{ color: '#3C3226', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'underline', textUnderlineOffset: '4px' }} data-testid="warm-check-seat-btn">Check if your seat is available <ArrowRight size={14} /></button>
          </Reveal>
        </div>
      </section>

      {/* ════════ 3. PROFESSION SHOWCASE — 8-profession auto-rotation ════════ */}
      <ProfessionShowcase />

      {/* ════════ 4. HOW IT WORKS ════════ */}
      <section id="how-it-works" className="flex items-center px-6 md:px-10" style={{ minHeight: '100vh', backgroundColor: '#EDE8DF' }} data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto w-full py-24">
          <Reveal>
            <p className="text-center mb-16" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '11px', color: '#B8985A', textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 500 }}>HOW IT WORKS</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-16 md:gap-20">
            {[
              { num: '01', title: 'We scan', desc: '9 AI engines query your business and your competitors simultaneously. ChatGPT, Claude, Gemini, Perplexity, and five more.' },
              { num: '02', title: 'You see', desc: 'Your AI Recommendation Score shows exactly which engines mention you, which mention your competitors, and why.' },
              { num: '03', title: 'We watch and build', desc: 'Every week, we re-scan all 9 engines, track competitor movements, generate fresh content, and alert you when anything changes. Your AI presence is continuously strengthened.' },
            ].map((step, i) => (
              <Reveal key={step.num} delay={i * 0.15}>
                <div data-testid={`how-step-${step.num}`}>
                  <span style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, color: '#B8985A', fontSize: '72px', lineHeight: 1 }}>{step.num}</span>
                  <h3 className="mt-4 mb-3" style={{ color: '#3C3226', fontSize: '16px', fontWeight: 500 }}>{step.title}</h3>
                  <p style={{ color: '#5C5347', fontSize: '14px', fontWeight: 300, lineHeight: 1.7 }}>{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ 4.5. MID-PAGE CLOSE (NEW) ════════ */}
      <MidPageClose />

      {/* ══════════════════════════════════════
          ACT 2 — "The world is changing beneath your feet"
          HARD CUT. No gradient. The plumber walks
          through a door they didn't expect.
         ══════════════════════════════════════ */}
      <div style={{ backgroundColor: '#1A1A2E' }} data-testid="act-two-container">
        {/* 5. The Shift */}
        <TheShift variant="dark" />

        {/* 6. The Proof */}
        <TheProof />

        {/* Dark scarcity — Three seats per city */}
        <section className="py-24 md:py-32 px-6 flex items-center justify-center" data-testid="scarcity-section">
          <div className="text-center max-w-xl mx-auto">
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.3em', color: '#F0EBE3', fontSize: 'clamp(24px, 4vw, 44px)', lineHeight: 1.2 }} className="mb-8">Three seats per profession, per city.</h2>
            <p style={{ color: 'rgba(240,235,227,0.6)', fontSize: '16px', fontWeight: 300, lineHeight: 1.8, maxWidth: '500px', margin: '0 auto' }} className="mb-8">We work with a select number of professionals in each market. When a plumber in {visitorCity} claims their seat, no other plumber in {visitorCity} can take it.</p>
            <div className="flex items-center justify-center gap-4 mb-8">
              {[{ claimed: true, initial: 'M' }, { claimed: true, initial: 'R' }, { claimed: false, initial: '' }].map((seat, i) => (
                <div key={i} className="flex items-center justify-center" style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: seat.claimed ? 'rgba(240,235,227,0.08)' : 'transparent', border: seat.claimed ? '1px solid rgba(240,235,227,0.2)' : '2px dashed rgba(240,235,227,0.2)', color: '#F0EBE3', fontSize: '14px', fontWeight: 600 }} data-testid={`seat-${i}`}>{seat.initial}</div>
              ))}
            </div>
            <button onClick={handleScan} className="inline-flex items-center gap-1" style={{ color: '#F0EBE3', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'underline', textUnderlineOffset: '4px' }} data-testid="check-seat-btn">Check if your seat is available <ArrowRight size={14} /></button>
          </div>
        </section>

        {/* 7. The Engine */}
        <TheEngine />

        {/* Soft 200px gradient — the relief of returning to warmth */}
        <div
          style={{ height: '200px', background: 'linear-gradient(to bottom, #1A1A2E, #EDE8DF)' }}
          data-testid="act-two-gradient"
        />
      </div>

      {/* ══════════════════════════════════════
          ACT 3 — "We're your bridge to that future"
          The warmth returns, but now paired
          with technology proof.
         ══════════════════════════════════════ */}

      {/* ════════ 8. WHAT WE BUILD FOR YOU ════════ */}
      <WhatWeBuild />

      {/* ════════ 9. THE NUMBERS ════════ */}
      <section className="flex items-center justify-center px-6" style={{ minHeight: '80vh', backgroundColor: '#EDE8DF' }} data-testid="numbers-section">
        <div className="max-w-4xl mx-auto w-full">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 text-center">
            {[{ value: 9, label: 'AI ENGINES MONITORED' }, { value: 14, label: 'QUERIES PER SCAN' }, { value: 0, label: 'CONTINUOUS MONITORING', display: 'Weekly' }, { value: 3, label: 'SEATS PER CITY' }].map((stat, i) => (
              <Reveal key={stat.label} delay={i * 0.12}>
                <div data-testid={`stat-${i}`}>
                  <p style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, color: '#3C3226', fontSize: 'clamp(44px, 6vw, 80px)', lineHeight: 1 }}>{stat.display || <AnimatedCounter target={stat.value} />}</p>
                  <p className="mt-3" style={{ color: '#5C5347', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em' }}>{stat.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ 9.5. MONITORING STATEMENT ════════ */}
      <section className="py-20 px-6 flex items-center justify-center" style={{ backgroundColor: '#EDE8DF' }} data-testid="monitoring-statement">
        <Reveal>
          <div className="text-center max-w-lg mx-auto">
            <p style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, color: '#3C3226', fontSize: 'clamp(22px, 3vw, 28px)', lineHeight: 1.5, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Your AI presence isn't a project. It's a practice.
            </p>
            <p className="mt-6" style={{ color: '#5C5347', fontSize: '15px', lineHeight: 1.8 }}>
              The scan shows you where you stand. The platform keeps you there. Every week, we re-scan all 9 AI engines, track competitor movements, generate fresh content, and alert you when anything changes.
            </p>
          </div>
        </Reveal>
      </section>

      {/* ════════ 10. TESTIMONIAL ════════ */}
      <TestimonialSection />

      {/* ════════ 11. FINAL CTA ════════ */}
      <section className="flex items-center justify-center px-6" style={{ minHeight: '60vh', backgroundColor: '#EDE8DF', paddingBottom: '80px' }} data-testid="final-cta-section">
        <Reveal>
          <div className="text-center max-w-xl mx-auto">
            <h2
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.2em', color: '#3C3226', fontSize: 'clamp(24px, 3vw, 32px)' }}
              className="mb-4"
              data-testid="final-cta-headline"
            >
              THE FUTURE IS ALREADY CHOOSING.
            </h2>
            <p style={{ color: '#5C5347', fontSize: '15px' }} className="mb-8">
              AI is mentioning your competitors right now. Find out where you stand in 60 seconds.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch gap-0 max-w-lg mx-auto">
              <input type="text" value={scanInput} onChange={e => setScanInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleScan()} placeholder="Enter your business name or website" className="flex-1 px-5 py-4 text-[15px] outline-none" style={{ backgroundColor: 'rgba(247,244,239,0.95)', border: '1px solid #D4C9B8', borderRight: 'none', borderRadius: 0, color: '#3C3226' }} data-testid="final-scan-input" />
              <button onClick={handleScan} className="px-8 py-4 text-[13px] font-medium uppercase tracking-widest whitespace-nowrap transition-opacity hover:opacity-90" style={{ backgroundColor: '#2C2520', color: '#F0EBE3', borderRadius: 0 }} data-testid="final-cta-btn">Scan now <ArrowRight size={14} className="inline ml-1" /></button>
            </div>
            {/* Scarcity circles */}
            <div className="flex items-center justify-center gap-3 mt-8">
              {[{ claimed: true, initial: 'M' }, { claimed: true, initial: 'R' }, { claimed: false, initial: '' }].map((seat, i) => (
                <div key={i} className="flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: seat.claimed ? '#F7F4EF' : 'transparent', border: seat.claimed ? '1px solid #D4C9B8' : '2px dashed #D4C9B8', color: '#3C3226', fontSize: '12px', fontWeight: 600 }} data-testid={`final-seat-${i}`}>{seat.initial}</div>
              ))}
            </div>
            <p className="mt-3" style={{ color: '#8C7E6E', fontSize: '12px' }} data-testid="final-scarcity-text">Only 1 plumber seat remains in {visitorCity}.</p>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="mt-6 flex items-center justify-center gap-2"
              style={{ fontSize: '11px', color: '#8C7E6E', letterSpacing: '0.05em' }}
              data-testid="social-proof-counter"
            >
              <span className="inline-flex -space-x-1.5">
                {['M', 'S', 'D', 'K'].map((i, idx) => (
                  <span key={idx} className="inline-flex items-center justify-center rounded-full" style={{ width: 20, height: 20, fontSize: '8px', fontWeight: 600, backgroundColor: '#EDE8DF', border: '1px solid #D4C9B8', color: '#5C5347' }}>{i}</span>
                ))}
              </span>
              Join {scanCount ? `${scanCount.toLocaleString()}+` : '2,400+'} professionals who've scanned their AI presence
            </motion.p>
          </div>
        </Reveal>
      </section>

      {/* ════════ 12. FOOTER ════════ */}
      <LandingFooter />

      {/* ════════ STICKY SCAN BAR ════════ */}
      <StickyScanBar />
    </div>
  );
}

/* ══════════════════════════════════════════
   SECTION 8: WHAT WE BUILD FOR YOU
   Bridge from Act 2 disruption to Act 3 resolution
   ══════════════════════════════════════════ */
function WhatWeBuild() {
  const visitorCity = useVisitorCity('Denver');
  return (
    <section className="flex items-center justify-center px-6 py-24" style={{ backgroundColor: '#EDE8DF' }} data-testid="what-we-build-section">
      <div className="max-w-3xl mx-auto w-full">
        {/* Header — bridges Act 2 into Act 3 */}
        <div className="text-center mb-12">
          <Reveal>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.2em', color: '#3C3226', fontSize: '28px' }}>WHAT WE CREATE ON YOUR BEHALF</h2>
            <p className="mt-4 mx-auto" style={{ color: '#5C5347', fontSize: '15px', maxWidth: '540px' }}>You saw the problem. You saw our engine. Now see what we build to get you recommended — automatically, every week.</p>
          </Reveal>
        </div>

        {/* 2x2 Artifact Grid */}
        <div className="grid md:grid-cols-2 gap-5 max-w-[700px] mx-auto">
          {/* Card 1: AI-Optimized Content */}
          <Reveal delay={0}>
            <div className="p-6" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', borderRadius: '2px' }} data-testid="artifact-content">
              <div className="mb-4 p-4" style={{ backgroundColor: '#FDFBF7', border: '1px solid #DDD5C8', borderRadius: '2px' }}>
                <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '14px', color: '#3C3226', lineHeight: 1.4 }}>Emergency Plumbing in {visitorCity}: What to Do Before the Plumber Arrives.</p>
                <p className="mt-2" style={{ fontSize: '11px', color: '#5C5347', lineHeight: 1.5 }}>When a pipe bursts at 2am, most homeowners panic. But the first 10 minutes are critical for minimizing damage...</p>
                <span className="inline-block mt-3 px-2 py-1 text-[10px] font-medium" style={{ backgroundColor: 'rgba(29,107,79,0.08)', color: '#1D6B4F' }}>FAQ Schema included</span>
              </div>
              <p style={{ fontSize: '11px', color: '#8C7E6E', lineHeight: 1.5 }}>We generate AI-optimized content structured for mention by ChatGPT, Gemini, and Claude.</p>
            </div>
          </Reveal>

          {/* Card 2: Review Coaching */}
          <Reveal delay={0.1}>
            <div className="p-6" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', borderRadius: '2px' }} data-testid="artifact-reviews">
              <div className="mb-4">
                <div className="space-y-2">
                  {['How was your experience?', 'What stood out most?', 'Post to Google'].map((step, i) => (
                    <div key={step} className="flex items-center gap-2.5">
                      <div className="flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0" style={{ backgroundColor: i < 2 ? '#1D6B4F' : 'rgba(29,107,79,0.15)', color: i < 2 ? '#fff' : '#1D6B4F', fontSize: '9px', fontWeight: 600 }}>{i + 1}</div>
                      <p style={{ fontSize: '12px', color: i < 2 ? '#3C3226' : '#8C7E6E', lineHeight: 1.4 }}>{step}</p>
                      {i < 2 && <CheckCircle2 size={11} color="#1D6B4F" className="ml-auto flex-shrink-0" />}
                    </div>
                  ))}
                </div>
              </div>
              <p style={{ fontSize: '11px', color: '#8C7E6E', lineHeight: 1.5 }}>Personalized review requests you can send to recent customers — with guided coaching that helps them write reviews AI engines trust.</p>
            </div>
          </Reveal>

          {/* Card 3: Directory Guidance */}
          <Reveal delay={0.2}>
            <div className="p-6" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', borderRadius: '2px' }} data-testid="artifact-directory">
              <div className="mb-4 space-y-2.5">
                {[{ dir: 'Google Business', status: 'Claimed', done: true }, { dir: 'Yelp', status: 'Not yet', done: false }, { dir: 'Apple Maps', status: 'Not yet', done: false }].map(d => (
                  <div key={d.dir} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {d.done ? <CheckCircle2 size={12} color="#1D6B4F" /> : <div className="w-3 h-3 rounded-full border" style={{ borderColor: '#DDD5C8' }} />}
                      <p style={{ fontSize: '12px', color: d.done ? '#3C3226' : '#8C7E6E' }}>{d.dir}</p>
                    </div>
                    {!d.done && <span style={{ fontSize: '10px', color: '#B8985A', fontWeight: 500 }}>Claim now &rarr;</span>}
                    {d.done && <span style={{ fontSize: '10px', color: '#1D6B4F', fontWeight: 500 }}>Verified</span>}
                  </div>
                ))}
              </div>
              <p style={{ fontSize: '11px', color: '#8C7E6E', lineHeight: 1.5 }}>We show you exactly which directories matter, prepare your information, and guide you through claiming your listings on 15+ platforms AI engines trust.</p>
            </div>
          </Reveal>

          {/* Card 4: Gap Analysis */}
          <Reveal delay={0.3}>
            <div className="p-6" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', borderRadius: '2px' }} data-testid="artifact-gaps">
              <div className="mb-4 space-y-2.5">
                {[
                  { text: 'Missing Google Business description', pts: '+8 pts', color: '#C67A5C' },
                  { text: 'No FAQ schema', pts: '+12 pts', color: '#B8985A' },
                  { text: 'Only 3 reviews (competitor: 47)', pts: '+15 pts', color: '#C67A5C' },
                ].map(gap => (
                  <div key={gap.text} className="flex items-start gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: gap.color }} />
                    <p style={{ fontSize: '12px', color: '#3C3226', lineHeight: 1.4 }}>{gap.text} <span className="font-semibold" style={{ color: '#1D6B4F' }}>&#8212; {gap.pts}</span></p>
                  </div>
                ))}
                <p className="text-right font-semibold mt-2" style={{ fontSize: '13px', color: '#1D6B4F' }}>+35 pts possible</p>
              </div>
              <p style={{ fontSize: '11px', color: '#8C7E6E', lineHeight: 1.5 }}>We identify every gap between you and the businesses AI currently mentions — and show you exactly how to close them.</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════
   SECTION 10: TESTIMONIAL
   ══════════════════════════════════════════ */
function TestimonialSection() {
  const [idx, setIdx] = useState(0);
  const t = TESTIMONIALS[idx];
  return (
    <section className="relative flex items-center justify-center overflow-hidden" style={{ minHeight: '100vh' }} data-testid="testimonial-section">
      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          <motion.img key={idx} src={t.img} alt={t.name} className="w-full h-full object-cover" style={{ filter: 'brightness(0.45)' }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} />
        </AnimatePresence>
        <div className="absolute inset-0" style={{ background: 'rgba(44,37,32,0.3)' }} />
      </div>
      <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
        <AnimatePresence mode="wait">
          <motion.div key={idx} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.4 }}>
            <p style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontWeight: 400, color: '#F0EBE3', fontSize: 'clamp(22px, 3vw, 32px)', lineHeight: 1.5 }} className="mb-8">"{t.quote}"</p>
            <p style={{ color: '#F0EBE3', fontSize: '13px' }}><span className="font-medium">{t.name}</span><span className="mx-2" style={{ color: '#8C7E6E' }}>—</span><span style={{ color: '#D4C9B8' }}>{t.prof}</span></p>
          </motion.div>
        </AnimatePresence>
        <div className="flex items-center justify-center gap-4 mt-10">
          <button onClick={() => setIdx(p => (p - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)} className="w-10 h-10 flex items-center justify-center" style={{ border: '1px solid rgba(240,235,227,0.3)' }} data-testid="testimonial-prev"><ChevronLeft size={16} color="#F0EBE3" /></button>
          {TESTIMONIALS.map((_, i) => (<button key={i} onClick={() => setIdx(i)} className="transition-all" style={{ width: i === idx ? 24 : 6, height: 2, backgroundColor: i === idx ? '#B8985A' : 'rgba(240,235,227,0.3)' }} data-testid={`testimonial-dot-${i}`} />))}
          <button onClick={() => setIdx(p => (p + 1) % TESTIMONIALS.length)} className="w-10 h-10 flex items-center justify-center" style={{ border: '1px solid rgba(240,235,227,0.3)' }} data-testid="testimonial-next"><ChevronRight size={16} color="#F0EBE3" /></button>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════
   HERO WITH PARALLAX
   Photo scales subtly as you scroll down
   ══════════════════════════════════════════ */
function HeroSection({ scanInput, setScanInput, handleScan }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section ref={ref} className="relative flex items-center justify-center overflow-hidden" style={{ minHeight: '100vh' }} data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
        <img src={PHOTOS.hero} alt="Professional at work" className="w-full h-full object-cover" style={{ filter: 'brightness(0.65)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(44,37,32,0.4) 0%, rgba(44,37,32,0.15) 50%, rgba(44,37,32,0.5) 100%)' }} />
      </motion.div>
      <motion.div className="relative z-10 text-center px-6 max-w-4xl mx-auto" style={{ opacity: contentOpacity }}>
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.25em', color: '#F0EBE3', fontSize: 'clamp(32px, 5vw, 72px)', lineHeight: 1.15 }} data-testid="hero-headline">
          AI is choosing who gets your customers. Take back control.
        </motion.h1>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mx-auto mt-8" style={{ width: 60, height: 1, backgroundColor: 'rgba(184,152,90,0.5)' }} />
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }} style={{ color: 'rgba(240,235,227,0.75)', fontSize: '16px', fontWeight: 300, lineHeight: 1.7, maxWidth: '560px', margin: '0 auto' }} className="mb-0 mt-14">Right now, when someone asks ChatGPT, Gemini, Claude, or Perplexity for a recommendation in your area, AI picks the answer. Find out if it's picking you.</motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.7 }} className="flex flex-col sm:flex-row items-stretch gap-0 max-w-xl mx-auto" style={{ marginTop: '50px' }}>
          <input type="text" value={scanInput} onChange={e => setScanInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleScan()} placeholder="Enter your business name or website" className="flex-1 px-5 py-4 text-[15px] outline-none" style={{ backgroundColor: 'rgba(247,244,239,0.95)', border: '1px solid rgba(212,201,184,0.5)', borderRight: 'none', borderRadius: 0, color: '#3C3226' }} data-testid="hero-scan-input" />
          <button onClick={handleScan} className="px-8 py-4 text-[13px] font-medium uppercase tracking-widest whitespace-nowrap transition-opacity hover:opacity-90" style={{ backgroundColor: '#2C2520', color: '#F0EBE3', borderRadius: 0 }} data-testid="hero-cta-btn">Scan now <ArrowRight size={14} className="inline ml-1" /></button>
        </motion.div>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="mt-4" style={{ color: '#8C7E6E', fontSize: '12px' }}>Free. 60 seconds. No credit card.</motion.p>
      </motion.div>
    </section>
  );
}
